import Notification from '../handlers/Notification';

const isBlank = (value) => value === '' || value === null || value === undefined;

const toNumber = (value) => (isBlank(value) ? NaN : Number(value));

const money = (value, currency) => `${currency}${Number(value).toFixed(2)}`;

export const getPriceField = (typeCoupon) => (typeCoupon === 'prc' ? 'price_prc' : 'price_e2');

export const getDeliveryPriceRules = (coupon) =>
	(coupon && coupon.config && coupon.config.delivery_price_rules) || [];

export const hasDeliveryPriceRules = (coupon) => getDeliveryPriceRules(coupon).length > 0;

export const newDeliveryPriceRule = (typeCoupon) => ({
	min_e2: '',
	max_e2: '',
	price_e2: typeCoupon !== 'prc' ? '' : null,
	price_prc: typeCoupon === 'prc' ? '' : null,
	is_fixed: typeCoupon === 'fixed',
});

export const convertRulesToType = (rules, typeCoupon) =>
	rules.map((rule) => {
		const currentValue = !isBlank(rule.price_prc) ? rule.price_prc : rule.price_e2;
		const value = isBlank(currentValue) ? '' : currentValue;

		return {
			...rule,
			price_e2: typeCoupon !== 'prc' ? value : null,
			price_prc: typeCoupon === 'prc'
				? (value === '' ? '' : Math.min(Math.round(Number(value)), 100))
				: null,
			is_fixed: typeCoupon === 'fixed',
		};
	});

export const sanitizeRuleValue = (name, value) => {
	if (name === 'price_prc') {
		const digits = String(value).replace(/[^0-9]/g, '');
		return digits === '' ? '' : Math.min(Number(digits), 100);
	}

	let cleanValue = String(value).replace(/[^0-9.]/g, '');
	const parts = cleanValue.split('.');
	if (parts.length > 2) {
		cleanValue = `${parts[0]}.${parts.slice(1).join('')}`;
	}
	const [integer, decimals] = cleanValue.split('.');
	if (decimals !== undefined) {
		cleanValue = `${integer || '0'}.${decimals.slice(0, 2)}`;
	}
	return cleanValue;
};

export const formatRuleAmount = (value) => (isBlank(value) || isNaN(Number(value)) ? value : Number(value).toFixed(2));

export const validateDeliveryPriceRules = (rules, typeCoupon, currency = '$') => {
	const priceField = getPriceField(typeCoupon);
	const rowErrors = rules.map(() => ({}));
	const messages = [];

	const addError = (index, field, message, summary = `Rango ${index + 1}: ${message}`) => {
		if (!rowErrors[index][field]) {
			rowErrors[index][field] = message;
			messages.push(summary);
		}
	};

	rules.forEach((rule, index) => {
		const min = toNumber(rule.min_e2);
		const max = toNumber(rule.max_e2);
		const price = toNumber(rule[priceField]);

		if (isNaN(min)) addError(index, 'min_e2', 'Ingrese el monto Desde');
		if (isNaN(max)) addError(index, 'max_e2', 'Ingrese el monto Hasta');
		if (!isNaN(min) && !isNaN(max) && max <= min) {
			addError(index, 'max_e2', 'Hasta debe ser mayor que Desde');
		}

		if (isNaN(price)) {
			addError(index, 'price', 'Ingrese el cobro a cliente');
		} else if (typeCoupon === 'prc') {
			if (price > 100) addError(index, 'price', 'El porcentaje no puede superar 100%');
		} else if (!isNaN(max) && price > max) {
			addError(index, 'price', `El cobro a cliente no puede superar el monto del envío (${money(max, currency)})`);
		}
	});

	let hasOverlap = false;
	const validRanges = rules
		.map((rule, index) => ({ index, min: toNumber(rule.min_e2), max: toNumber(rule.max_e2) }))
		.filter(({ min, max }) => !isNaN(min) && !isNaN(max) && max > min);

	validRanges.forEach((a, i) => {
		validRanges.slice(i + 1).forEach((b) => {
			const from = Math.max(a.min, b.min);
			const to = Math.min(a.max, b.max);
			if (from <= to) {
				hasOverlap = true;
				const tramo = from === to
					? `en ${money(from, currency)}`
					: `entre ${money(from, currency)} y ${money(to, currency)}`;
				rowErrors[a.index].overlapped = true;
				rowErrors[b.index].overlapped = true;
				addError(
					a.index,
					'overlap',
					`Este rango se solapa con el de ${money(b.min, currency)} a ${money(b.max, currency)} ${tramo}.`,
					`El rango ${a.index + 1} se solapa con el rango ${b.index + 1} ${tramo}`
				);
			}
		});
	});

	return {
		rowErrors,
		messages,
		hasOverlap,
		isValid: messages.length === 0,
	};
};

export const rulesToApi = (rules) =>
	(rules || []).map((rule) => ({
		min_e2: Math.round(Number(rule.min_e2) * 100),
		max_e2: Math.round(Number(rule.max_e2) * 100),
		price_e2: isBlank(rule.price_e2) ? null : Math.round(Number(rule.price_e2) * 100),
		price_prc: isBlank(rule.price_prc) ? null : Number((Number(rule.price_prc) / 100).toFixed(4)),
		is_fixed: !!rule.is_fixed,
	}));

export const rulesFromApi = (rules) =>
	(rules || []).map((rule) => ({
		min_e2: Number(rule.min_e2) / 100,
		max_e2: Number(rule.max_e2) / 100,
		price_e2: isBlank(rule.price_e2) ? null : Number(rule.price_e2) / 100,
		price_prc: isBlank(rule.price_prc) ? null : Math.round(Number(rule.price_prc) * 100),
		is_fixed: !!rule.is_fixed,
	}));

export const withHumanDeliveryPriceRules = (coupon) =>
	coupon && coupon.config && coupon.config.delivery_price_rules
		? { ...coupon, config: { ...coupon.config, delivery_price_rules: rulesFromApi(coupon.config.delivery_price_rules) } }
		: coupon;

export const canSaveDeliveryPriceRules = (coupon, typeCoupon, currency) => {
	if (!hasDeliveryPriceRules(coupon)) return true;

	const { isValid, messages } = validateDeliveryPriceRules(getDeliveryPriceRules(coupon), typeCoupon, currency);
	if (!isValid) {
		Notification.show({
			type: 'warning',
			title: 'Cobro a cliente por rango de precio',
			msg: messages.join('. '),
		});
	}
	return isValid;
};

export const getCouponType = (coupon) => {
	const rules = getDeliveryPriceRules(coupon);
	const isPrc = rules.length > 0
		? rules.some(rule => !isBlank(rule.price_prc))
		: !!(coupon && coupon.human_price_prc);

	if (isPrc) return 'prc';
	return coupon && coupon.config && coupon.config.is_fixed ? 'fixed' : 'e2';
};



// WEBPACK FOOTER //
// ./src/utils/deliveryPriceRules.js